import { useCallback, useEffect } from 'react';

export enum OutboundPageTreeMessage {
	ContentUpdated,
}

type OutboundPageTreeMessageRegistry = {
	[OutboundPageTreeMessage.ContentUpdated]: {
		contentId: string;
		parentId?: string;
	};
};

class OutboundPageTreeMessageBus {
	private subscribers: Record<string, Array<Function>> = {};

	publish<MessageType extends keyof OutboundPageTreeMessageRegistry>(
		type: MessageType,
		data: OutboundPageTreeMessageRegistry[MessageType],
	) {
		if (!this.subscribers[type]) {
			return;
		}

		this.subscribers[type].forEach((subscriber) => subscriber(data));
	}

	subscribe<MessageType extends keyof OutboundPageTreeMessageRegistry>(
		type: MessageType,
		callback: (data: OutboundPageTreeMessageRegistry[MessageType]) => void,
	) {
		if (!this.subscribers[type]) {
			this.subscribers[type] = [];
		}
		this.subscribers[type].push(callback);
	}

	unsubscribe<MessageType extends keyof OutboundPageTreeMessageRegistry>(
		type: MessageType,
		callback: (data: OutboundPageTreeMessageRegistry[MessageType]) => void,
	) {
		if (!this.subscribers[type]) {
			return;
		}
		this.subscribers[type] = this.subscribers[type].filter((subscriber) => subscriber !== callback);
	}
}

const outboundPageTreeBus = new OutboundPageTreeMessageBus();

export const useOutboundPageTreeUpdatePublisher = <
	MessageType extends keyof OutboundPageTreeMessageRegistry,
>(
	type: MessageType,
) => {
	return useCallback(
		(data: OutboundPageTreeMessageRegistry[MessageType]) => {
			outboundPageTreeBus.publish(type, data);
		},
		[type],
	);
};

export const useOutboundPageTreeUpdateSubscriber = <
	MessageType extends keyof OutboundPageTreeMessageRegistry,
>(
	type: MessageType,
	callback: (data: OutboundPageTreeMessageRegistry[MessageType]) => void,
) => {
	useEffect(() => {
		outboundPageTreeBus.subscribe(type, callback);

		return () => {
			outboundPageTreeBus.unsubscribe(type, callback);
		};
	}, [callback, type]);
};
